import { useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useOutletContext } from "react-router"
import { useLens } from "@hookform/lenses"
import { useFieldArray } from "@hookform/lenses/rhf"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  EpisodeOfCareRelatedConditionschema,
  episodeOfCareRelatedConditionsFormSchema,
  LinkedConditionInformation,
  RelatedConditionsForm,
} from "@/modules/patients/detail/episode-of-care/components/related-conditions-form"
import {
  EpisodeOfCareIcdCodeForm,
  episodeOfCareIcdCodeFormSchema,
} from "@/modules/patients/detail/episode-of-care/episode-of-care-icd-code-form"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import { handleAppendRelatedConditions } from "@/modules/patients/detail/episode-of-care/lib/handle-append-related-conditions"
import { handleUpdateAllPatientCondition } from "@/modules/patients/detail/episode-of-care/lib/handle-update-all-patient-condition"
import {
  EpisodeOfCareFragment$data,
  EpisodeOfCareFragment$key,
} from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { useEditEpisodeOfCareDialogStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareDialogStore"
import { useGetExcludedIcdCodes } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useFetchIcdCodesQuery } from "@/modules/patients/hooks/useIcdDiagnosisQuery"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { IcdDiagnosisFragment$data } from "@/modules/patients/services/__generated__/IcdDiagnosisFragment.graphql"
import {
  PatientConditionInformationOutputFragment$data,
  PatientConditionInformationOutputFragment$key,
} from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { editEpisodeOfCare } from "@/modules/patients/services/editEpisodeOfCare"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientCondition } from "@/modules/patients/services/usePatientCondition"
import { useUpdateAllPatientConditionsMutation } from "@/modules/patients/services/useUpdateAllPatientConditionsMutation"
import { Country } from "@/types/__generated__/union-types"

const editEpisodeOfCareFormSchema = episodeOfCareRelatedConditionsFormSchema.extend(
  episodeOfCareIcdCodeFormSchema.shape,
)

type EditEpisodeOfCareSchema = z.infer<typeof editEpisodeOfCareFormSchema>

interface EditEpisodeOfCareDialogProps {
  country: Country
  onDialogOpenChange: () => void
}

const getDefaultRelatedConditionsFormData = (
  episodeOfCareRelatedConditions: EpisodeOfCareFragment$data["relatedConditions"],
  patientConditions: PatientConditionInformationOutputFragment$data,
): EpisodeOfCareRelatedConditionschema["relatedConditions"] => {
  const res: EpisodeOfCareRelatedConditionschema["relatedConditions"] = []

  episodeOfCareRelatedConditions.forEach((e) => {
    res.push({
      icdCode: e.icdDiagnosis.diagnosisCode,
      description: e.icdDiagnosis.text,
      value: true,
      isFromIcdSelectInThisSession: false,
    })
  })

  patientConditions.conditions.forEach((e) => {
    if (!episodeOfCareRelatedConditions.some((c) => c.icdDiagnosis.diagnosisCode === e.icdDiagnosis.diagnosisCode)) {
      res.push({
        icdCode: e.icdDiagnosis.diagnosisCode,
        description: e.icdDiagnosis.text,
        value: false,
        isFromIcdSelectInThisSession: false,
      })
    }
  })

  return res.sort((a, b) => a.icdCode.localeCompare(b.icdCode))
}

const EditEpisodeOfCareDialog = ({ country, onDialogOpenChange }: EditEpisodeOfCareDialogProps) => {
  const { t } = useTranslation()
  const patientId = usePatientParam()

  const handleUpdateAllCondition = useUpdateAllPatientConditionsMutation(country)

  const [hasAutoFocusOpened, setHasAutoFocusOpened] = useState(false)
  const [linkedConditionInformation, setLinkedConditionInformation] = useState<LinkedConditionInformation | undefined>()

  const episodeOfCareRef = useEditEpisodeOfCareDialogStoreContext((state) => state.episodeOfCareRef)

  if (!episodeOfCareRef) {
    throw new Error("EpisodeOfCareRef is null")
  }

  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()
  const { id, icdDiagnosis, relatedConditions } = useFragment<EpisodeOfCareFragment$key>(
    getEpisodeOfCareFragment(country),
    episodeOfCareRef,
  )

  const dataRef = usePatientCondition(patientId, country)
  const patientConditionData = useFragment<PatientConditionInformationOutputFragment$key>(
    PatientConditionInformationOutputFragment,
    dataRef,
  )

  const excludedIcdCodes = useGetExcludedIcdCodes()
  const { icdCodes, debounceSearch, isLoading } = useFetchIcdCodesQuery({
    birthDay: patient.birthDate,
    excludedIcdCodes,
    gender: patient.gender,
    searchType: "EPISODE_OF_CARE",
  })

  const form = useForm<EditEpisodeOfCareSchema>({
    resolver: zodResolver(editEpisodeOfCareFormSchema),
    defaultValues: {
      diagnosisCode: icdDiagnosis.diagnosisCode,
      relatedConditions: getDefaultRelatedConditionsFormData(relatedConditions, patientConditionData),
    },
  })

  const { handleSubmit, getValues, control } = form

  const lens = useLens({ control })
  const relatedConditionsLen = lens.focus("relatedConditions")

  const { append: appendRelatedCondition, fields: relatedConditionFields } = useFieldArray(
    relatedConditionsLen.interop(),
  )

  const onSubmit = (data: EditEpisodeOfCareSchema) => {
    const relatedConditions = data.relatedConditions.filter((e) => e.value).map((e) => e.icdCode)

    editEpisodeOfCare(country, { diagnosisCode: data.diagnosisCode, id, relatedConditions }, () => {
      handleUpdateAllPatientCondition(patientId, patientConditionData, data.relatedConditions, handleUpdateAllCondition)
    })

    onDialogOpenChange()
  }

  const appendRelatedConditions = useCallback(
    (option: IcdDiagnosisFragment$data) => {
      const relatedConditions = getValues("relatedConditions")

      handleAppendRelatedConditions(option, relatedConditions, setLinkedConditionInformation, appendRelatedCondition)
    },
    [getValues, appendRelatedCondition],
  )

  if (!episodeOfCareRef) {
    return null
  }

  return (
    <Dialog open onOpenChange={onDialogOpenChange}>
      <DialogContentCustom
        variant="small"
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          setHasAutoFocusOpened(true)
        }}
        data-testid="edit-episode-of-care-dialog">
        <DialogHeader>
          <DialogTitle>{t("episodeOfCare.editEpisodeOfCare.title")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-6 py-2">
          <FormProvider {...form}>
            <div className="relative">
              <EpisodeOfCareIcdCodeForm
                autoFocus={hasAutoFocusOpened}
                options={icdCodes ?? { commons: [], others: [] }}
                onChange={debounceSearch}
                selectedIcdCode={icdDiagnosis}
                isFetchingIcdCodes={isLoading}
                onOptionSelect={appendRelatedConditions}
              />
            </div>
            <RelatedConditionsForm
              lens={relatedConditionsLen}
              fields={relatedConditionFields}
              linkedConditionInformation={linkedConditionInformation}
            />
          </FormProvider>
        </div>

        <DialogFooter className="flex p-6 pt-4">
          <Button
            className="px-4"
            data-testid="episode-of-care-cancel-btn"
            variant="secondary"
            onClick={onDialogOpenChange}>
            {t("common.button.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            className="px-4"
            data-testid="episode-of-care-save-btn">
            {t("common.button.save")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export { EditEpisodeOfCareDialog }
